// src/pages/Admin/AdminPayments.tsx - COMPLETE UPDATED VERSION with ID formatting
import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../api';
import { motion } from 'framer-motion';
import { Search, CreditCard, Calendar, Download } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils';
import { formatDisplayId } from '../../utils/idGenerator';
import toast from 'react-hot-toast';

interface Payment {
  _id: string;
  orderId: string;
  customerId: { fullName: string; email: string };
  riderId: { fullName: string; email: string };
  totalPrice: number;
  platformFee: number;
  riderPayout: number;
  commitmentPaidAt: string;
  offlinePaidAt?: string;
}

const AdminPayments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  useEffect(() => {
    fetchPayments();
  }, [page]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getFinancialDashboard({});
      if (res.data.success) {
        setPayments(res.data.data.recentPayments);
        setTotalPages(Math.ceil(res.data.data.recentPayments.length / limit));
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = payments.filter(p =>
    formatDisplayId(p.orderId).toLowerCase().includes(search.toLowerCase()) ||
    p.customerId?.fullName?.toLowerCase().includes(search.toLowerCase()) ||
    p.riderId?.fullName?.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedPayments = filteredPayments.slice((page - 1) * limit, page * limit);

  const totalPlatformFees = payments.reduce((sum, p) => sum + (p.platformFee || 0), 0);
  const totalRiderPayouts = payments.reduce((sum, p) => sum + (p.riderPayout || 0), 0);

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Payments</h1>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <p className="text-sm text-gray-600 mb-1">Total Platform Fees</p>
          <p className="text-3xl font-bold text-emerald-600">{formatCurrency(totalPlatformFees)}</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <p className="text-sm text-gray-600 mb-1">Total Rider Payouts</p>
          <p className="text-3xl font-bold text-blue-600">{formatCurrency(totalRiderPayouts)}</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <p className="text-sm text-gray-600 mb-1">Total Transactions</p>
          <p className="text-3xl font-bold text-gray-900">{payments.length}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl p-4 shadow-lg mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by Order ID or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Order ID</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Customer</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Rider</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Total</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Platform Fee</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Rider Payout</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">10% Paid</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">90% Paid</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginatedPayments.map((p) => (
                    <tr key={p._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm">
                        <span title={p.orderId}>{formatDisplayId(p.orderId)}</span>
                      </td>
                      <td className="px-6 py-4">{p.customerId?.fullName || 'N/A'}</td>
                      <td className="px-6 py-4">{p.riderId?.fullName || 'N/A'}</td>
                      <td className="px-6 py-4 font-bold">{formatCurrency(p.totalPrice)}</td>
                      <td className="px-6 py-4 text-emerald-600">{formatCurrency(p.platformFee)}</td>
                      <td className="px-6 py-4 text-blue-600">{formatCurrency(p.riderPayout)}</td>
                      <td className="px-6 py-4 text-sm">{p.commitmentPaidAt ? formatDate(p.commitmentPaidAt) : '-'}</td>
                      <td className="px-6 py-4 text-sm">{p.offlinePaidAt ? formatDate(p.offlinePaidAt) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-6 gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPayments;